/**********************
 * Content events Events
 *********************/
import React, { useEffect, useState, useRef } from "react"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"

import {
  acCreateUpdateEvent,
  acCancelCalendarEvent,
  acUnlinkCalendarEvent,
  acDeleteCalendarEvent,
} from "helpers/backend_helper"
import {
  getLocale,
  getLottieLoadingOptions,
  showToast,
  contentImageFormatter,
} from "utils/general"

import SelectVirtualRoom from "../Components/SelectVirtualRoom"
import EventRepetitionSelect from "../Components/EventRepetitionSelect"
import EventImagePicker from "../Components/EventImagePicker"

import {
  Button,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
} from "reactstrap"
import { Table as AntdTable, Tooltip, ConfigProvider, DatePicker, Form as AntdForm, Input as AntdInput, InputNumber } from "antd"

import * as Yup from "yup"
import { useFormik } from "formik"
import Lottie from "react-lottie"
import dayjs from "dayjs"

const EventsContent = props => {
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [selectedEventId, setSelectedEventId] = useState(null)
  const [selectedEventSerie, setSelectedEventSerie] = useState(null)

  const [eventModal, setEventModal] = useState(false)
  const [cancelModal, setCancelModal] = useState(false)
  const [unlinkModal, setUnlinkModal] = useState(false)
  const [deleteEventModal, setDeleteEventModal] = useState(false)

  const [checkOverlappingEvent, setCheckOverlappingEvent] = useState(true)
  const [overlappingEventModal, setOverlappingEventModal] = useState(false)

  const locale = getLocale()

  const abortRef = useRef(null)
  useEffect(() => {
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    if (!eventModal) eventValidation.resetForm()
  }, [eventModal])

  // Cancel event
  const cancelEvent = (id, serie) => {
    if (!props.ongoingAction && id != null) {
      props.setOngoingAction(true)
      let eventId = Number.parseInt(id, 10)
      let serieId = serie != undefined ? Number.parseInt(serie, 10) : undefined

      acCancelCalendarEvent(
        abortRef.current,
        eventId,
        serieId,
        props.isProduction
      )
        .then(result => {
          //console.log("Cancel result: ", result)
          if (result.statusCode == 200) {
            showToast(props.t("Event canceled"), "success")
            props.getEvents(props.selectedTreeOption)
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          props.setOngoingAction(false)
          setCancelModal(false)
          setSelectedEventId(null)
          setSelectedEventSerie(null)
        })
    }
  }

  // Unlink event
  const unlinkEvent = id => {
    if (!props.ongoingAction && id != null) {
      props.setOngoingAction(true)
      let eventId = Number.parseInt(id, 10)
      acUnlinkCalendarEvent(abortRef.current, eventId, props.isProduction)
        .then(result => {
          if (result.statusCode == 200) {
            showToast(props.t("Updated"), "success")
            props.getEvents(props.selectedTreeOption)
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          props.setOngoingAction(false)
          setUnlinkModal(false)
          setSelectedEventId(null)
          setSelectedEventSerie(null)
        })
    }
  }

  // Delete event
  const deleteEvent = (id, serie) => {
    if (!props.ongoingAction && id != null) {
      props.setOngoingAction(true)
      let eventId = Number.parseInt(id, 10)
      let serieId = serie != undefined ? Number.parseInt(serie, 10) : undefined

      acDeleteCalendarEvent(
        abortRef.current,
        eventId,
        serieId,
        props.isProduction
      )
        .then(result => {
          //console.log("Delete result: ", result)
          if (result.statusCode == 200) {
            props.getEvents(props.selectedTreeOption)
            showToast(props.t("Deleted"), "success")
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          props.setOngoingAction(false)
          setDeleteEventModal(false)
          setSelectedEventId(null)
          setSelectedEventSerie(null)
        })
    }
  }

  // New event validation init
  const setNewEvent = () => {
    let newEntry = new Object()
    newEntry.id = undefined
    newEntry.serieId = undefined
    newEntry.virtualRoom = null
    newEntry.start = dayjs().add(1, "day").startOf("day").add(8, "hour")
    newEntry.duration = 60
    newEntry.imageBase64 = ""
    setSelectedEvent(newEntry)
    setEventModal(true)
    if (!props.virtualsLoaded && !props.loadingVirtuals) {
      props.getVirtualRooms(props.selectedTreeOption)
    }
  }

  // Event validation
  const eventValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: selectedEvent?.id,
      serieId: selectedEvent?.serieId,
      virtualRoom: selectedEvent?.virtualRoom || null,
      title: selectedEvent?.title || "",
      start: selectedEvent?.start || null,
      duration: selectedEvent?.duration || 60,
      imageBase64: selectedEvent?.imageBase64 || "",
      recursionCount: 5,
      recursionInterval: 0,
      updateRecurring: false,
    },
    validationSchema: Yup.object({
      title: Yup.string()
        .required(props.t("Required field"))
        .trim()
        .min(5, props.t("Too short"))
        .max(30, props.t("Too long")),
      duration: Yup.number()
        .required(props.t("Required field"))
        .min(10, props.t("Too short"))
        .max(1440, props.t("Too long")),
    }),
    onSubmit: values => {
      //console.log("Submit event: ", values)
      let imageChanged = values.imageBase64 != selectedEvent?.imageBase64
      let checkOverlap = checkOverlappingEvent
      setCheckOverlappingEvent(true)
      if (
        !props.ongoingAction &&
        props.selectedTreeOption &&
        values.virtualRoom
      ) {
        props.setOngoingAction(true)
        acCreateUpdateEvent(
          abortRef.current,
          props.selectedTreeOption,
          values.title,
          values.start.valueOf(),
          values.duration,
          values.virtualRoom,
          values.recursionInterval,
          values.recursionCount,
          imageChanged ? values.imageBase64 : undefined,
          values.id,
          values.updateRecurring ? values.serieId : undefined,
          props.isProduction,
          checkOverlap
        )
          .then(result => {
            //console.log("Add or update event result: ", result)
            if (result.statusCode === 200 && result.id) {
              props.getEvents(props.selectedTreeOption)
              setEventModal(false)
            } else if (
              result.statusCode === 400 &&
              result.status == "overlapping_event"
            ) {
              setOverlappingEventModal(true)
            } else {
              showToast(props.t("Error"), "error")
            }
          })
          .catch(err => {
            console.log(err)
            showToast(props.t("Error"), "error")
          })
          .finally(() => {
            props.setOngoingAction(false)
          })
      }
    },
  })

  const eventColumns = [
    {
      dataIndex: "image_base64",
      title: props.t("Image"),
      align: "center",
      render: contentImageFormatter,
      width: "50px",
    },
    {
      dataIndex: "date",
      title: props.t("Date"),
      width: "70px",
      ellipsis: true,
      render: (_, record) =>
        `${dayjs(record.start).format("dd")} ${record.date}`,
    },
    {
      dataIndex: "time",
      title: props.t("klo"),
      render: eventTimeFormatter,
      width: "80px",
      ellipsis: true,
    },
    {
      dataIndex: "title",
      title: props.t("Title"),
      render: titleFormatter,
      width: "100px",
      ellipsis: true,
    },
    {
      dataIndex: "room",
      title: props.t("Room"),
      width: "80px",
      ellipsis: true,
    },
    {
      dataIndex: "actions",
      title: props.t("Actions"),
      align: "center",
      render: eventEditFormatter,
      //fixed: "right",
      width: "100px",
      ellipsis: true,
    },
  ]

  function eventTimeFormatter(cell, row) {
    if (row.canceled)
      return <span className="text-danger">{props.t("Event canceled")}</span>
    else return cell
  }

  function titleFormatter(cell, row) {
    return (
      <div className="d-flex" style={{ alignItems: "center" }}>
        <span>{cell}</span>
        {row.recursion_serie && row.recursion_serie != "0" && (
          <div
            className="avatar-xs ms-2"
            style={{ width: "26px", height: "26px" }}
          >
            <div className="avatar-title bg-light rounded-circle text-primary">
              <i className="mdi mdi-repeat font-size-14" />
            </div>
          </div>
        )}
      </div>
    )
  }

  function eventEditFormatter(cell, row) {
    return (
      <>
        {!row.canceled && (
          <>
            <Tooltip
              title={props.t("Edit event")}
              placement="bottom"
              trigger={["hover", "focus"]}
            >
              <Button
                color="primary"
                outline
                disabled={props.ongoingAction}
                size="sm"
                onClick={() => {
                  let entry = new Object()
                  entry.id = row.id
                  entry.serieId = row.recursion_serie
                  entry.virtualRoom = row.vmr_id
                  entry.title = row.title
                  entry.duration = row.duration
                  entry.start = dayjs(row.start)
                  entry.imageBase64 = row.image_base64
                  setSelectedEvent(entry)
                  setEventModal(true)
                  if (!props.virtualsLoaded && !props.loadingVirtuals) {
                    props.getVirtualRooms(props.selectedTreeOption)
                  }
                }}
              >
                <i className="mdi mdi-pencil" />
              </Button>
            </Tooltip>

            {row.recursion_serie && row.recursion_serie != "0" && (
              <Tooltip
                title={props.t("Unlink event")}
                placement="bottom"
                trigger={["hover", "focus"]}
              >
                <Button
                  className="ms-2"
                  color="danger"
                  outline
                  disabled={props.ongoingAction}
                  size="sm"
                  onClick={() => {
                    setSelectedEventId(row.id)
                    setUnlinkModal(true)
                  }}
                >
                  <i className="mdi mdi-link-off" />
                </Button>
              </Tooltip>
            )}
            <Tooltip
              title={props.t("Cancel event")}
              placement="bottom"
              trigger={["hover", "focus"]}
            >
              <Button
                className="ms-2"
                color="danger"
                outline
                disabled={props.ongoingAction}
                size="sm"
                onClick={() => {
                  setSelectedEventId(row.id)
                  setSelectedEventSerie(row.recursion_serie)
                  setCancelModal(true)
                }}
              >
                <i className="mdi mdi-cancel" />
              </Button>
            </Tooltip>
          </>
        )}
        <Tooltip
          title={props.t("Delete event")}
          placement="bottom"
          trigger={["hover", "focus"]}
        >
          <Button
            className="ms-2"
            color="danger"
            outline
            disabled={props.ongoingAction}
            size="sm"
            onClick={() => {
              setSelectedEventId(row.id)
              setSelectedEventSerie(row.recursion_serie)
              setDeleteEventModal(true)
            }}
          >
            <i className="mdi mdi-delete" />
          </Button>
        </Tooltip>
      </>
    )
  }

  // Selected virtual room changed
  const roomSelectionChanged = value => {
    eventValidation.setFieldValue("virtualRoom", value)
  }

  // Repetition changed
  const repetitionChanged = value => {
    eventValidation.setFieldValue("recursionInterval", Number.parseFloat(value))
  }

  return (
    <>
      {/* ACCORDION */}
      {props.loadingEvents ? (
        <>
          <div className="text-center">
            <Lottie
              options={getLottieLoadingOptions()}
              height={80}
              width={80}
            />
          </div>
        </>
      ) : (
        <>
          <Row>
            <Col>
              <div className="d-flex" style={{ alignItems: "center" }}>
                <div
                  className="avatar-xs ms-2"
                  style={{
                    width: "26px",
                    height: "26px",
                  }}
                >
                  <div className="avatar-title bg-light rounded-circle text-primary">
                    <i className="mdi mdi-repeat font-size-14" />
                  </div>
                </div>
                <span className="text-muted ms-2">
                  {props.t("Recurring event")}
                </span>
              </div>
            </Col>
            <Col></Col>
            <Col className="col-auto">
              <Button
                color="success"
                size="sm"
                className="d-flex"
                style={{ alignItems: "center" }}
                onClick={() => {
                  setNewEvent()
                }}
              >
                <i className="me-2 bx bx-calendar-plus text-truncate align-middle"></i>
                <span className="">{props.t("New event")}</span>
              </Button>
            </Col>
          </Row>
          <Row className="mt-2 mb-3">
            <Col>
              <AntdTable
                className="clear-header"
                size="small"
                rowKey={"id"}
                columns={eventColumns}
                dataSource={props.events}
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: 50,
                  hideOnSinglePage: true,
                }}
                tableLayout="auto"
                scroll={{
                  x: "max-content",
                  y: "35vh",
                }}
              />
            </Col>
          </Row>
        </>
      )}

      {/* MODALS */}
      {/** Event modal */}
      <Modal
        isOpen={eventModal}
        toggle={() => {
          setEventModal(!eventModal)
        }}
        size="lg"
        centered
      >
        <div className="modal-header">
          {selectedEvent?.id != undefined ? (
            <h5 className="modal-title mt-0" id="myModalLabel">
              {props.t("Edit event")}
            </h5>
          ) : (
            <h5 className="modal-title mt-0" id="myModalLabel">
              {props.t("New event")}
            </h5>
          )}
          <button
            type="button"
            onClick={() => {
              setEventModal(false)
            }}
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <>
          <div className="modal-body">
            <Form
              onSubmit={e => {
                e.preventDefault()
                return false
              }}
            >
              <Row xs={1} lg={2} className="mb-2">
                <FormGroup>
                  <Label>{props.t("Virtual room")}</Label>
                  <SelectVirtualRoom
                    data={props.virtualRooms}
                    loadingData={props.loadingVirtuals}
                    selection={eventValidation.values.virtualRoom}
                    setSelectedRoom={roomSelectionChanged}
                    selectedGroupId={props.selectedTreeOption}
                  />
                  <Input
                    hidden
                    invalid={eventValidation.values.virtualRoom == null}
                  />
                  <FormFeedback type="invalid">
                    {props.t("Required field")}
                  </FormFeedback>
                </FormGroup>

                <FormGroup>
                  <Label>{props.t("Title")}</Label>
                  <AntdForm.Item
                    validateStatus={eventValidation.errors.title ? "error" : ""}
                    hasFeedback
                    help={eventValidation.errors.title}
                  >
                    <AntdInput
                      allowClear
                      showCount
                      maxLength={30}
                      placeholder={props.t("Title")}
                      value={eventValidation.values.title}
                      onChange={(v) => { eventValidation.setFieldValue("title", v.target.value) }}
                    />
                  </AntdForm.Item>
                </FormGroup>

                <FormGroup>
                  <Label>{props.t("Start")}</Label>
                  <ConfigProvider locale={locale}>
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD.MM.YYYY HH:mm"
                      showTime
                      allowClear={false}
                      value={eventValidation.values.start}
                      onChange={v => eventValidation.setFieldValue("start", v)}
                    />
                  </ConfigProvider>
                </FormGroup>
                <FormGroup>
                  <Label>{props.t("Duration")}</Label>
                  <InputNumber
                    value={eventValidation.values.duration}
                    suffix={props.t("minutes")}
                    min={10}
                    max={1440}
                    style={{ width: "100%" }}
                    onChange={(v) => { eventValidation.setFieldValue("duration", v) }}
                  />
                </FormGroup>

                {!eventValidation.values.id && (
                  <>
                    <FormGroup>
                      <Label>{props.t("Repetition")}</Label>
                      <EventRepetitionSelect setSelected={repetitionChanged} />
                    </FormGroup>

                    {eventValidation.values.recursionInterval > 0 && (
                      <FormGroup>
                        <Label>{props.t("Repetition count")}</Label>
                        <InputNumber
                          value={eventValidation.values.recursionCount}
                          min={1}
                          max={100}
                          style={{ width: "100%" }}
                          onChange={(v) => { eventValidation.setFieldValue("recursionCount", v) }}
                        />
                      </FormGroup>
                    )}
                  </>
                )}
              </Row>

              <EventImagePicker
                selected={eventValidation.values.imageBase64}
                selectionChanged={img => {
                  eventValidation.setFieldValue("imageBase64", img)
                }}
              />

              <ModalFooter className="p-2 mt-2">
                <Col lg={12} className="mt-2 mb-0  text-truncate">
                  <Row>
                    <Col xs={12}>
                      <span className="float-end">
                        <button
                          type="button"
                          onClick={() => {
                            setEventModal(false)
                          }}
                          className="btn btn-outline-primary "
                          data-dismiss="modal"
                        >
                          {props.t("Cancel")}
                        </button>

                        {eventValidation.values.serieId &&
                          eventValidation.values.serieId != "0" && (
                            <button
                              disabled={
                                props.ongoingAction || props.loadingVirtuals
                              }
                              className="btn btn-outline-primary ms-2"
                              onClick={() => {
                                eventValidation.setFieldValue(
                                  "updateRecurring",
                                  true
                                )
                                eventValidation.submitForm()
                              }}
                            >
                              {props.t("Update recurring serie")}
                            </button>
                          )}

                        <button
                          disabled={
                            props.ongoingAction || props.loadingVirtuals
                          }
                          className="btn btn-primary ms-2"
                          onClick={() => {
                            eventValidation.setFieldValue(
                              "updateRecurring",
                              false
                            )
                            eventValidation.submitForm()
                          }}
                        >
                          {eventValidation.values.id
                            ? props.t("UpdateCommand")
                            : props.t("Save")}
                        </button>
                      </span>
                    </Col>
                  </Row>
                </Col>
              </ModalFooter>
            </Form>
          </div>
        </>
      </Modal>

      {/** Cancel event modal */}
      <Modal
        isOpen={cancelModal}
        toggle={() => {
          setCancelModal(!cancelModal)
        }}
        centered
      >
        <div className="modal-header">
          <h5 className="modal-title mt-0" id="myModalLabel">
            {props.t("Cancel event")}
          </h5>
          <button
            type="button"
            onClick={() => {
              setCancelModal(false)
            }}
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <ModalBody>
          {props.t("Are you sure you want to cancel the selected event?")}
        </ModalBody>
        <ModalFooter className="p-2 mt-2">
          <Col lg={12} className="mt-2 mb-0  text-truncate">
            <Row>
              <Col xs={12}>
                <span className="float-end">
                  <button
                    type="button"
                    onClick={() => {
                      setCancelModal(false)
                    }}
                    className="btn btn-outline-primary "
                    data-dismiss="modal"
                  >
                    {props.t("No")}
                  </button>

                  {selectedEventSerie && selectedEventSerie != "0" && (
                    <button
                      disabled={props.ongoingAction}
                      className="btn btn-outline-danger ms-2"
                      onClick={() => {
                        cancelEvent(selectedEventId, selectedEventSerie)
                      }}
                    >
                      {props.t("Cancel recurring serie")}
                    </button>
                  )}

                  <button
                    disabled={props.ongoingAction}
                    className="btn btn-danger ms-2"
                    onClick={() => {
                      cancelEvent(selectedEventId, undefined)
                    }}
                  >
                    {props.t("Yes")}
                  </button>
                </span>
              </Col>
            </Row>
          </Col>
        </ModalFooter>
      </Modal>

      {/** Unlink event modal */}
      <Modal
        isOpen={unlinkModal}
        toggle={() => {
          setUnlinkModal(!unlinkModal)
        }}
        centered
      >
        <div className="modal-header">
          <h5 className="modal-title mt-0" id="myModalLabel">
            {props.t("Unlink event")}
          </h5>
          <button
            type="button"
            onClick={() => {
              setUnlinkModal(false)
            }}
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <ModalBody>
          {props.t(
            "Are you sure you want to unlink the selected event from the recurring serie?"
          )}
        </ModalBody>
        <ModalFooter className="p-2 mt-2">
          <Col lg={12} className="mt-2 mb-0  text-truncate">
            <Row>
              <Col xs={12}>
                <span className="float-end">
                  <button
                    type="button"
                    onClick={() => {
                      setUnlinkModal(false)
                    }}
                    className="btn btn-outline-primary "
                    data-dismiss="modal"
                  >
                    {props.t("No")}
                  </button>

                  <button
                    disabled={props.ongoingAction}
                    className="btn btn-danger ms-2"
                    onClick={() => {
                      unlinkEvent(selectedEventId)
                    }}
                  >
                    {props.t("Yes")}
                  </button>
                </span>
              </Col>
            </Row>
          </Col>
        </ModalFooter>
      </Modal>

      {/** Overlapping event warning modal */}
      <Modal
        isOpen={overlappingEventModal}
        toggle={() => {
          setOverlappingEventModal(!overlappingEventModal)
        }}
        centered
      >
        <div className="modal-header">
          <h5 className="modal-title mt-0" id="myModalLabel">
            {props.t("Overlapping event")}
          </h5>
          <button
            type="button"
            onClick={() => {
              setOverlappingEventModal(false)
            }}
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <ModalBody>{props.t("OverlappingEventDescription")}</ModalBody>
        <ModalFooter className="p-2 mt-2">
          <Col lg={12} className="mt-2 mb-0  text-truncate">
            <Row>
              <Col xs={12}>
                <span className="float-end">
                  <button
                    type="button"
                    onClick={() => {
                      setOverlappingEventModal(false)
                    }}
                    className="btn btn-outline-primary "
                    data-dismiss="modal"
                  >
                    {props.t("Cancel")}
                  </button>

                  <button
                    disabled={props.ongoingAction}
                    className="btn btn-primary ms-2"
                    onClick={() => {
                      setOverlappingEventModal(false)
                      setCheckOverlappingEvent(false)
                      eventValidation.submitForm()
                    }}
                  >
                    {props.t("Yes")}
                  </button>
                </span>
              </Col>
            </Row>
          </Col>
        </ModalFooter>
      </Modal>

      {/** Delete event modal */}
      <Modal
        isOpen={deleteEventModal}
        toggle={() => {
          setDeleteEventModal(!deleteEventModal)
        }}
        centered
      >
        <div className="modal-header">
          <h5 className="modal-title mt-0" id="myModalLabel">
            {props.t("Delete event")}
          </h5>
          <button
            type="button"
            onClick={() => {
              setDeleteEventModal(false)
            }}
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <ModalBody>
          {props.t("Are you sure you want to delete the selected event?")}
        </ModalBody>
        <ModalFooter className="p-2 mt-2">
          <Col lg={12} className="mt-2 mb-0  text-truncate">
            <Row>
              <Col xs={12}>
                <span className="float-end">
                  <button
                    type="button"
                    onClick={() => {
                      setDeleteEventModal(false)
                    }}
                    className="btn btn-outline-primary "
                    data-dismiss="modal"
                  >
                    {props.t("Cancel")}
                  </button>

                  {selectedEventSerie && selectedEventSerie != "0" && (
                    <button
                      disabled={props.ongoingAction}
                      className="btn btn-outline-danger ms-2"
                      onClick={() => {
                        deleteEvent(selectedEventId, selectedEventSerie)
                      }}
                    >
                      {props.t("Delete recurring serie")}
                    </button>
                  )}

                  <button
                    disabled={props.ongoingAction}
                    className="btn btn-danger ms-2"
                    onClick={() => {
                      deleteEvent(selectedEventId, undefined)
                    }}
                  >
                    {props.t("Delete")}
                  </button>
                </span>
              </Col>
            </Row>
          </Col>
        </ModalFooter>
      </Modal>
    </>
  )
}

EventsContent.propTypes = {
  t: PropTypes.any,
  isProduction: PropTypes.any,
  ongoingAction: PropTypes.bool,
  setOngoingAction: PropTypes.any,
  events: PropTypes.any,
  getEvents: PropTypes.func,
  loadingEvents: PropTypes.bool,
  virtualRooms: PropTypes.any,
  loadingVirtuals: PropTypes.bool,
  virtualsLoaded: PropTypes.bool,
  selectedTreeOption: PropTypes.any,
  getVirtualRooms: PropTypes.func,
}

export default withTranslation()(EventsContent)
